import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronUp, Download, Clock, Lock, FileInput, GitCompareArrows, Loader2 } from 'lucide-react'
import { useVersionList } from '../../hooks/useNegotiations'
import { downloadVersion, downloadRedline } from '../../services/negotiationsApi'
import { writeNegotiationContext } from '../../hooks/useCustomXmlParts'
import { devLog } from '../../services/devLog'

interface VersionHistoryPanelProps {
  negotiationId: number
  /** Negotiation title — used for download filenames */
  title?: string
  /** When true, skip accordion header and show content immediately (tab mode) */
  alwaysExpanded?: boolean
  /** Watermark type applied to downloads (from negotiation) */
  watermark?: string
}

/** Convert a Blob to a base64 string (without data URI prefix). */
async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const result = reader.result as string
      // Strip "data:...;base64," prefix
      resolve(result.split(',')[1] || result)
    }
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

export function VersionHistoryPanel({ negotiationId, title: negotiationTitle, alwaysExpanded, watermark }: VersionHistoryPanelProps) {
  const { t } = useTranslation()
  const { data, isLoading } = useVersionList(negotiationId)
  const [isExpanded, setIsExpanded] = useState(alwaysExpanded ?? false)
  const [downloadingVersion, setDownloadingVersion] = useState<number | null>(null)
  const [confirmOpen, setConfirmOpen] = useState<number | null>(null)
  const [isInserting, setIsInserting] = useState(false)
  const [insertError, setInsertError] = useState<string | null>(null)
  const [comparingVersion, setComparingVersion] = useState<number | null>(null)

  const isDesktop = Office.context.platform !== Office.PlatformType.OfficeOnline

  /** Download and insert version into the current Word document (Desktop only). */
  const handleOpenInWord = async (versionNumber: number) => {
    setIsInserting(true)
    setConfirmOpen(null)
    setInsertError(null)
    try {
      devLog.info('Version open', `Downloading v${versionNumber}...`)
      const blob = await downloadVersion(negotiationId, versionNumber)
      const base64 = await blobToBase64(blob)

      devLog.info('Version open', `Inserting v${versionNumber} into document...`)
      await Word.run(async (context) => {
        const body = context.document.body
        body.clear()
        await context.sync()
        body.insertFileFromBase64(base64, Word.InsertLocation.start)
        await context.sync()
      })

      // Update negotiation context XML parts to reflect the loaded version
      await writeNegotiationContext({
        negotiation_id: negotiationId,
        current_version: versionNumber,
        title: negotiationTitle || '',
      })

      devLog.info('Version open', `v${versionNumber} loaded into document`)
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      devLog.error('Version open', `Failed: ${msg}`)
      setInsertError(msg)
    } finally {
      setIsInserting(false)
    }
  }

  /** Fallback: save to Downloads folder (browser download). */
  const handleDownloadToFile = async (versionNumber: number) => {
    setDownloadingVersion(versionNumber)
    try {
      const blob = await downloadVersion(negotiationId, versionNumber)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${negotiationTitle || 'version'}_v${versionNumber}.docx`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      // Download failed
    } finally {
      setDownloadingVersion(null)
    }
  }

  /** Download a redline DOCX comparing this version with the previous one. */
  const handleCompareWithPrevious = async (versionNumber: number) => {
    if (versionNumber <= 1) return
    setComparingVersion(versionNumber)
    try {
      const blob = await downloadRedline(negotiationId, versionNumber - 1, versionNumber)
      if (isDesktop) {
        // Insert redline into Word document
        const base64 = await blobToBase64(blob)
        await Word.run(async (context) => {
          const body = context.document.body
          body.clear()
          await context.sync()
          body.insertFileFromBase64(base64, Word.InsertLocation.start)
          await context.sync()
        })
      } else {
        // Download as file for Word Online
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${negotiationTitle || 'redline'}_v${versionNumber - 1}-v${versionNumber}.docx`
        a.click()
        URL.revokeObjectURL(url)
      }
    } catch {
      devLog.warn('Compare', `Failed to download redline for v${versionNumber}`)
    }
    setComparingVersion(null)
  }

  if (isLoading || !data) return null

  const { versions, total_count, has_more } = data
  const displayVersions = isExpanded ? versions : versions.slice(0, 3)

  const expanded = alwaysExpanded || isExpanded

  return (
    <div className={alwaysExpanded ? '' : 'border-b border-neutral-100 dark:border-neutral-800'}>
      {!alwaysExpanded && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-3 py-1.5 flex items-center gap-1.5 text-[10px] font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors"
        >
          <Clock size={10} />
          {t('negotiate.versionHistory')}
          <span className="text-[10px] text-neutral-400 normal-case">
            ({total_count})
          </span>
          <span className="flex-1" />
          {isExpanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
        </button>
      )}

      {/* Watermark info */}
      {watermark && watermark !== 'NONE' && expanded && (
        <div className="mx-3 mb-1 px-2 py-1 rounded bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 flex items-center gap-1.5">
          <Lock size={10} className="text-amber-500 shrink-0" />
          <span className="text-[10px] text-amber-700 dark:text-amber-400">
            {t('negotiate.watermarkInfo', 'Downloaded with {{watermark}} watermark', { watermark })}
          </span>
        </div>
      )}

      {/* Confirm dialog */}
      {confirmOpen != null && (
        <div className="mx-3 mb-2 p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
          <p className="text-[10px] font-medium text-amber-700 dark:text-amber-300">
            {t('negotiate.replaceConfirm', 'Replace current document with v{{version}}?', { version: confirmOpen })}
          </p>
          <p className="text-[9px] text-amber-600/80 dark:text-amber-400/80 mt-0.5">
            {t('negotiate.replaceWarning', 'Unsaved changes in the current document will be lost.')}
          </p>
          <div className="flex gap-2 mt-1.5">
            <button
              onClick={() => handleOpenInWord(confirmOpen)}
              disabled={isInserting}
              className="px-2 py-1 text-[10px] font-medium rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
            >
              {isInserting
                ? t('negotiate.replacing', 'Loading...')
                : t('negotiate.replaceConfirmButton', 'Replace')}
            </button>
            <button
              onClick={() => setConfirmOpen(null)}
              className="px-2 py-1 text-[10px] text-amber-600 dark:text-amber-400 hover:underline"
            >
              {t('common.cancel', 'Cancel')}
            </button>
          </div>
        </div>
      )}

      {/* Insert error */}
      {insertError && (
        <div className="mx-3 mb-2 p-2 rounded-lg bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800">
          <p className="text-[10px] font-medium text-error-700 dark:text-error-300">
            {t('negotiate.openFailed', 'Could not open version in Word')}
          </p>
          <p className="text-[9px] text-error-600/80 dark:text-error-400/80 mt-0.5 break-all">
            {insertError}
          </p>
          <button
            onClick={() => setInsertError(null)}
            className="mt-1 text-[9px] text-error-500 hover:underline"
          >
            {t('common.dismiss', 'Dismiss')}
          </button>
        </div>
      )}

      {/* Inserting overlay */}
      {isInserting && (
        <div className="mx-3 mb-2 flex items-center gap-2 p-2 rounded-lg bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800">
          <div className="w-3 h-3 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-[10px] text-primary-600 dark:text-primary-300">
            {t('negotiate.insertingVersion', 'Loading version into document...')}
          </span>
        </div>
      )}

      {expanded && (
        <div className="px-3 pb-2">
          {/* Word Online info */}
          {!isDesktop && (
            <div className="mb-2 p-1.5 rounded bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700">
              <p className="text-[9px] text-blue-700 dark:text-blue-300">
                {t('negotiate.wordOnlineHint', 'You are using Word Online. Download a version, edit it locally, then upload it back as a new version.')}
              </p>
            </div>
          )}

          {/* Vertical timeline */}
          <div className="relative ml-2">
            {/* Timeline line */}
            <div className="absolute left-0 top-1 bottom-1 w-px bg-neutral-200 dark:bg-neutral-700" />

            {displayVersions.map((version, index) => (
              <div key={version.id} className="relative flex items-start gap-2 py-1">
                {/* Timeline dot */}
                <div
                  className={`relative z-10 w-1.5 h-1.5 rounded-full mt-1 shrink-0 ${
                    index === 0
                      ? 'bg-primary-500'
                      : 'bg-neutral-300 dark:bg-neutral-600'
                  }`}
                  style={{ marginLeft: '-3px' }}
                />

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1">
                    <span className={`text-[10px] font-medium ${
                      index === 0
                        ? 'text-primary-600 dark:text-primary-400'
                        : 'text-neutral-600 dark:text-neutral-400'
                    }`}>
                      v{version.version_number}
                    </span>
                    {version.sent_by && (
                      <span className="text-[10px] text-neutral-400 truncate">
                        {t('negotiate.versionBy', { name: version.sent_by.display_name })}
                      </span>
                    )}
                    {version.upload_source === 'web' && (
                      <span className="text-[9px] px-1 py-px rounded bg-neutral-100 dark:bg-neutral-700 text-neutral-400">
                        web
                      </span>
                    )}
                    <span className="flex-1" />
                    {isDesktop && (
                      <button
                        onClick={() => setConfirmOpen(version.version_number)}
                        disabled={isInserting || confirmOpen != null}
                        className="p-0.5 text-neutral-400 hover:text-primary-500 disabled:opacity-50 transition-colors"
                        title={t('negotiate.openInWord', 'Open in Word')}
                      >
                        <FileInput size={10} />
                      </button>
                    )}
                    <button
                      onClick={() => handleDownloadToFile(version.version_number)}
                      disabled={downloadingVersion === version.version_number}
                      className="p-0.5 text-neutral-400 hover:text-primary-500 disabled:opacity-50 transition-colors"
                      title={t('negotiate.downloadVersion', 'Download')}
                    >
                      <Download size={10} />
                    </button>
                    {version.version_number > 1 && (
                      <button
                        onClick={() => handleCompareWithPrevious(version.version_number)}
                        disabled={comparingVersion === version.version_number}
                        className="p-0.5 text-neutral-400 hover:text-accent-500 disabled:opacity-50 transition-colors"
                        title={t('negotiate.comparePrevious', 'Compare with previous version')}
                      >
                        {comparingVersion === version.version_number ? (
                          <Loader2 size={10} className="animate-spin" />
                        ) : (
                          <GitCompareArrows size={10} />
                        )}
                      </button>
                    )}
                  </div>
                  <span className="text-[10px] text-neutral-400">
                    {new Date(version.created_at).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                  {version.note && (
                    <p className="text-[10px] text-neutral-500 dark:text-neutral-400 mt-0.5 line-clamp-2 italic">
                      {version.note}
                    </p>
                  )}
                  {version.upload_source === 'web' && version.paragraph_overlap_pct != null && version.paragraph_overlap_pct < 0.5 && (
                    <p className="text-[9px] text-amber-600 dark:text-amber-400 mt-0.5">
                      {t('negotiate.lowOverlap', '⚠ Document differs significantly ({{pct}}% match)', {
                        pct: Math.round(version.paragraph_overlap_pct * 100),
                      })}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {has_more && (
            <div className="mt-1.5 flex items-center gap-1 text-[10px] text-neutral-400">
              <Lock size={10} />
              <span>
                {t('negotiate.versionHistoryLocked', {
                  hidden: total_count - versions.length,
                  defaultValue: '+{{hidden}} earlier versions — upgrade for full history',
                })}
              </span>
            </div>
          )}

          {!has_more && versions.length > 3 && !isExpanded && (
            <button
              onClick={() => setIsExpanded(true)}
              className="mt-1 text-[10px] text-primary-500 hover:underline"
            >
              +{versions.length - 3} more
            </button>
          )}
        </div>
      )}
    </div>
  )
}
